import { useEffect } from 'react';

const SITE_ORIGIN = 'https://towshare.ca';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/TowShareLongLogo.png`;
const MANAGED_ATTR = 'data-page-meta';

export type PageMeta = {
  /** Final `<title>` — TowShare suffix appended automatically unless `rawTitle` is true. */
  title: string;
  description: string;
  /** Path-only canonical (e.g. `/trailer-rental/ottawa`). Origin is added. */
  canonicalPath?: string;
  /** Override the og:image URL. */
  ogImage?: string;
  /** noindex this page (for filtered/utility routes). */
  noindex?: boolean;
  /** Skip the " — TowShare" suffix if the title already includes the brand. */
  rawTitle?: boolean;
  /** JSON-LD blobs to render alongside the page (e.g. FAQPage, Service). */
  jsonLd?: Array<Record<string, unknown>>;
  /** Extra keyword meta. */
  keywords?: string;
};

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(MANAGED_ATTR, '1');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    el.setAttribute(MANAGED_ATTR, '1');
    document.head.appendChild(el);
  }
  el.href = href;
}

function clearManagedJsonLd() {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTR}]`)
    .forEach((n) => n.remove());
}

/**
 * Lightweight per-route `<head>` manager. Mutates document.title, the standard
 * meta tags, canonical link, and any page-scoped JSON-LD. No new dependencies —
 * works alongside the static defaults in index.html.
 */
export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const fullTitle = meta.rawTitle ? meta.title : `${meta.title} — TowShare`;
    document.title = fullTitle;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: meta.description,
    });

    if (meta.keywords) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: meta.keywords });
    }

    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: meta.noindex
        ? 'noindex,follow'
        : 'index,follow,max-image-preview:large,max-snippet:-1',
    });

    const canonical = meta.canonicalPath
      ? `${SITE_ORIGIN}${meta.canonicalPath}`
      : `${SITE_ORIGIN}${window.location.pathname}`;
    upsertLink('canonical', canonical);

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: meta.description,
    });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: meta.ogImage ?? DEFAULT_OG_IMAGE,
    });

    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: meta.description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: meta.ogImage ?? DEFAULT_OG_IMAGE,
    });

    clearManagedJsonLd();
    for (const blob of meta.jsonLd ?? []) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(MANAGED_ATTR, '1');
      script.text = JSON.stringify(blob);
      document.head.appendChild(script);
    }
  }, [
    meta.title,
    meta.description,
    meta.canonicalPath,
    meta.ogImage,
    meta.noindex,
    meta.rawTitle,
    meta.keywords,
    meta.jsonLd,
  ]);
}
